import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { ListChecks, ShieldCheck, ArrowRight, Brain } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function CreditApproverDashboard() {
  const [stats, setStats] = useState({ aip_accepted: 0, dd_setup_pending: 0, total: 0, disbursed: 0, declined: 0, gross_loans: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/loans/all");
        const s = { aip_accepted: 0, dd_setup_pending: 0, total: data.length, disbursed: 0, declined: 0, gross_loans: 0 };
        for (const l of data) {
          if (l.state === "aip_accepted") s.aip_accepted += 1;
          if (l.state === "dd_setup_pending") s.dd_setup_pending += 1;
          if (l.state === "disbursed") { s.disbursed += 1; s.gross_loans += (l.amount || 0); }
          if (l.state === "declined") s.declined += 1;
        }
        setStats(s);
        setRecent(data.slice(0, 5));
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardShell
      title="Credit Approver"
      subtitle="Two-eyes underwriting — review applications, issue offers, verify Direct Debit. Disbursement is signed off by Admin."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Awaiting offer" value={loading ? "…" : stats.aip_accepted} sub="aip_accepted → final_approved" accent="warning" />
        <StatCard label="Awaiting DD verify" value={loading ? "…" : stats.dd_setup_pending} sub="dd_setup_pending → dd_verified" accent="warning" />
        <StatCard label="Disbursed (lifetime)" value={loading ? "…" : stats.disbursed} sub={fmtNGN(stats.gross_loans)} accent="success" />
        <StatCard label="Total applications" value={loading ? "…" : stats.total} sub={`${stats.declined} declined`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Link to="/approver/loans" className="card hover:shadow-md transition-shadow group" data-testid="approver-action-underwriting">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 rounded-xl bg-[#0A3622] text-[#F2B632] grid place-items-center">
              <ListChecks size={22} weight="fill" />
            </div>
            <ArrowRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#0A3622]" />
          </div>
          <div className="font-display text-xl font-medium mt-4">Open underwriting queue</div>
          <p className="text-sm text-[#4F5D4F] mt-1">Review AI assessments, issue Loan Offer Letters, verify DD mandates.</p>
        </Link>

        <div className="card">
          <div className="w-12 h-12 rounded-xl bg-[#E2F3EB] text-[#2D7A5D] grid place-items-center">
            <Brain size={22} weight="fill" />
          </div>
          <div className="font-display text-xl font-medium mt-4">AI is your co-pilot</div>
          <p className="text-sm text-[#4F5D4F] mt-1">Every application includes a Claude-generated risk score, band, and reasoning. Use it as a tie-breaker, not a rubber stamp.</p>
        </div>

        <div className="card">
          <div className="w-12 h-12 rounded-xl bg-[#FBF1DC] text-[#B45309] grid place-items-center">
            <ShieldCheck size={22} weight="fill" />
          </div>
          <div className="font-display text-xl font-medium mt-4">Two-eyes principle</div>
          <p className="text-sm text-[#4F5D4F] mt-1">You issue offers and verify mandates. Admin signs off the actual cash release. No single person can approve and disburse.</p>
        </div>
      </div>

      <div className="card mt-7">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="overline">Recent applications</div>
            <h3 className="font-display text-lg font-medium mt-1">Latest 5 loans across the platform</h3>
          </div>
          <Link to="/approver/loans" className="btn-ghost text-sm" data-testid="approver-view-all">View all →</Link>
        </div>
        {loading ? (
          <p className="text-sm text-[#4F5D4F] py-6 text-center">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-6 text-center" data-testid="approver-no-loans">No loans yet.</p>
        ) : (
          <div className="divide-y divide-[#E5E3DB]">
            {recent.map((l) => (
              <div key={l.id} className="py-3 flex justify-between items-center" data-testid={`approver-recent-${l.id}`}>
                <div>
                  <div className="font-display font-medium">{fmtNGN(l.amount)} · {l.tenor_months} mo</div>
                  <div className="text-xs text-[#4F5D4F]">Band {l.ai_risk_band || "—"} · {l.state}</div>
                </div>
                <span className="text-xs font-mono text-[#4F5D4F]">{(l.id || "").slice(0, 8)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
